import { useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

export default function PracticeTopicsPage({
  domain,
  topics = [],
  activeSubjectPracticeSummaries = [],
  loading,
  openTopic,
  startNewSubjectPracticeRound,
  goBack,
  styles,
}) {
  const { t } = useLanguage();

  // =========================================================
  // FIND ACTIVE SESSION FOR A TOPIC
  // =========================================================

  const getActiveSummaryForTopic = (
    topicId
  ) => {
    return (
      activeSubjectPracticeSummaries.find(
        (summary) =>
          summary.topicId === topicId
      ) || null
    );
  };

  const [
    confirmTopic,
    setConfirmTopic,
  ] = useState(null);

  const [
    confirmSummary,
    setConfirmSummary,
  ] = useState(null);

  return (
    <div style={styles.card}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div style={styles.topBar}>
        <div>
          <div style={styles.quizPath}>
            {t("practice.practiceQuestions")} / {t("practice.practiceBySubject")}
          </div>

          <h2 style={styles.sectionTitle}>
            {domain?.domain_name || t("practice.topics")}
          </h2>

          <div
            style={{
              marginTop: "6px",
              color: "#6b7280",
              lineHeight: "1.6",
              maxWidth: "680px",
            }}
          >
            {t("practice.chooseTopicDesc")}
          </div>
        </div>

        <button
          style={styles.secondaryButton}
          onClick={goBack}
        >
          {t("practice.back")}
        </button>
      </div>

      {/* =====================================================
          LOADING / EMPTY
      ===================================================== */}

      {loading ? (
        <div
          style={{
            ...styles.emptyBox,
            marginTop: "24px",
          }}
        >
          {t("practice.loadingTopics")}
        </div>
      ) : topics.length === 0 ? (
        <div
          style={{
            ...styles.emptyBox,
            marginTop: "24px",
          }}
        >
          {t("practice.noTopicsFound")}
        </div>
      ) : (
        <div
          style={{
            marginTop: "28px",
            display: "grid",
            gap: "16px",
          }}
        >
          {topics.map((topic) => {
            const activeSummary =
              getActiveSummaryForTopic(
                topic.id
              );

            const hasActiveRound =
              Boolean(activeSummary);

            const completedQuestions =
              activeSummary
                ?.completedQuestions || 0;

            const totalQuestions =
              activeSummary
                ?.totalQuestions || 10;

            const progressPercent =
              totalQuestions > 0
                ? Math.round(
                    (
                      completedQuestions /
                      totalQuestions
                    ) * 100
                  )
                : 0;

            return (
              <div
                key={topic.id}
                style={{
                  border:
                    hasActiveRound
                      ? "1px solid #bfdbfe"
                      : "1px solid #e5e7eb",

                  borderRadius: "16px",

                  padding: "20px",

                  backgroundColor:
                    hasActiveRound
                      ? "#f8fbff"
                      : "#ffffff",

                  display: "flex",

                  flexDirection: "column",

                  gap: "14px",
                }}
              >
                {/* ===========================================
                    TOPIC HEADER
                =========================================== */}

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    gap: "16px",
                    alignItems:
                      "flex-start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#172033",
                      }}
                    >
                      {topic.topic_name}
                    </div>

                    {hasActiveRound && (
                      <div
                        style={{
                          marginTop: "5px",
                          color: "#2563eb",
                          fontSize: "13px",
                          fontWeight: "700",
                        }}
                      >
                        {t("practice.roundInProgress")}
                      </div>
                    )}
                  </div>

                  {hasActiveRound && (
                    <div
                      style={{
                        padding:
                          "5px 10px",
                        borderRadius:
                          "999px",
                        backgroundColor:
                          "#dbeafe",
                        color:
                          "#1d4ed8",
                        fontSize:
                          "12px",
                        fontWeight:
                          "800",
                      }}
                    >
                      {completedQuestions} /{" "}
                      {totalQuestions}
                    </div>
                  )}
                </div>

                {/* ===========================================
                    ACTIVE ROUND PROGRESS
                =========================================== */}

                {hasActiveRound && (
                  <div>
                    <div
                      style={{
                        height: "8px",
                        backgroundColor:
                          "#e2e8f0",
                        borderRadius:
                          "999px",
                        overflow:
                          "hidden",
                      }}
                    >
                      <div
                        style={{
                          width:
                            `${progressPercent}%`,
                          height: "100%",
                          backgroundColor:
                            "#2563eb",
                          borderRadius:
                            "999px",
                          transition:
                            "width 0.25s ease",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        marginTop: "8px",
                        color: "#64748b",
                        fontSize: "13px",
                      }}
                    >
                      {completedQuestions} / {totalQuestions}{" "}
                      {t("practice.questionsCompleted")}
                    </div>
                  </div>
                )}

                {/* ===========================================
                    ACTION
                =========================================== */}

                <button
                  style={{
                    ...(hasActiveRound
                      ? styles.primaryButton
                      : styles.secondaryButton),

                    width: "100%",

                    justifyContent:
                      "center",

                    padding:
                      "12px 16px",
                  }}
                  onClick={() =>
                    openTopic(topic)
                  }
                >
                  {hasActiveRound
                    ? t("practice.continuePractice")
                    : t("practice.startPractice")}
                </button>

                {hasActiveRound && (
                  <button
                    style={{
                      width: "100%",
                      border: "none",
                      backgroundColor:
                        "transparent",
                      color: "#64748b",
                      fontWeight: "700",
                      cursor: "pointer",
                      padding: "10px 12px",
                    }}
                    onClick={() => {
                      setConfirmTopic(
                        topic
                      );

                      setConfirmSummary(
                        activeSummary
                      );
                    }}
                  >
                    {t("practice.startNewRound")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {confirmTopic && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor:
              "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            zIndex: 9999,
          }}
          onClick={() => {
            setConfirmTopic(null);
            setConfirmSummary(null);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "460px",
              backgroundColor:
                "#ffffff",
              borderRadius: "20px",
              padding: "28px",
              boxShadow:
                "0 20px 60px rgba(15, 23, 42, 0.22)",
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div
              style={{
                fontSize: "34px",
                marginBottom: "14px",
              }}
            >
              ⚠️
            </div>

            <h3
              style={{
                margin: "0 0 10px",
                fontSize: "22px",
                color: "#172033",
              }}
            >
              {t("practice.startNewRoundTitle")}
            </h3>

            <p
              style={{
                margin: 0,
                color: "#64748b",
                lineHeight: "1.6",
              }}
            >
              {t("practice.subjectRoundWarningBefore")}{" "}
              <strong>
                {confirmTopic.topic_name}
              </strong>
              .{" "}
              {t("practice.subjectRoundWarningAfter")}
            </p>

            <div
              style={{
                marginTop: "24px",
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <button
                style={
                  styles.secondaryButton
                }
                onClick={() => {
                  const topicToContinue =
                    confirmTopic;

                  setConfirmTopic(null);
                  setConfirmSummary(null);

                  openTopic(
                    topicToContinue
                  );
                }}
              >
                {t("practice.continueCurrentRound")}
              </button>

              <button
                style={
                  styles.primaryButton
                }
                onClick={() => {
                  const topicToRestart =
                    confirmTopic;

                  const summaryToAbandon =
                    confirmSummary;

                  setConfirmTopic(null);
                  setConfirmSummary(null);

                  startNewSubjectPracticeRound(
                    topicToRestart,
                    summaryToAbandon
                  );
                }}
              >
                {t("practice.confirmStartNewRound")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}